import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { FaArrowLeft, FaBoxOpen, FaCheckCircle, FaLink, FaExclamationTriangle } from "react-icons/fa";
import api from "../../api";
import { Card, Button, DataTable, Modal, LoadingState, ErrorState } from "../../components/ui";
import { formatDateOnly, toDateOnlyString } from "../../utils/dateOnly";
import "./NewJoinerRequestDetailPage.css";

// Phase 52 (New Joiner Asset Request). Technician's single-request working
// view (task spec §19-24): employee/joining/manager-HR info, requested
// items with an inventory-fulfilment picker fed by the EXISTING canonical
// `GET /alloc/products` endpoint (never a second products list), timeline,
// and the 4 workflow actions -- each gated purely by whether the backend
// accepts the call (403/404 surfaces as an inline error), never by a
// client-side role guess.
const STATUS_LABEL = {
  SUBMITTED: "Submitted", ASSIGNED: "Assigned", IN_PREPARATION: "In Preparation",
  READY: "Ready", DEPLOYED: "Deployed", CANCELLED: "Cancelled",
};
const STATUS_TONE = {
  SUBMITTED: "gray", ASSIGNED: "blue", IN_PREPARATION: "amber",
  READY: "purple", DEPLOYED: "green", CANCELLED: "red",
};

function formatDateTime(value) {
  if (!value) return "-";
  try {
    return new Date(value).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch {
    return String(value);
  }
}
function todayIsoDate() {
  const formatter = new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Kolkata", year: "numeric", month: "2-digit", day: "2-digit" });
  return formatter.format(new Date());
}

// Presentational only -- label/value row used inside the Employee /
// Joining / Manager-HR info cards below. Renders nothing new the plain
// <p><strong>Label:</strong> value</p> markup didn't already show; just a
// consistent layout for it.
function InfoRow({ label, value }) {
  return (
    <div className="njr-info-row">
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function InfoCard({ title, children }) {
  return (
    <div className="njr-info-card">
      <h4>{title}</h4>
      {children}
    </div>
  );
}

// Purely a display tone for the timeline dot, derived from the same
// `h.event` text already rendered -- never a second source of truth for
// request status. Any event string not matching one of these stays the
// default (in-progress) tone.
function timelineTone(event) {
  const e = String(event || "").toUpperCase();
  if (e.includes("CANCEL")) return "cancelled";
  if (e.includes("DEPLOY") || e.includes("READY")) return "terminal";
  return "";
}

function ItemInventoryPicker({ item, unitId, onSaved }) {
  const [open, setOpen] = useState(false);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [selectedProductId, setSelectedProductId] = useState(item.products?.[0]?.product_id || "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const loadProducts = useCallback(() => {
    setLoading(true);
    api.get("/alloc/products", { params: { unit_id: unitId } })
      .then((res) => setProducts(Array.isArray(res.data?.data) ? res.data.data : (Array.isArray(res.data) ? res.data : [])))
      .catch(() => setProducts([]))
      .finally(() => setLoading(false));
  }, [unitId]);

  useEffect(() => { if (open) loadProducts(); }, [open, loadProducts]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return products.filter((p) => {
      if (Number(p.available_qty) <= 0 && Number(p.id) !== Number(item.products?.[0]?.product_id)) return false;
      if (!q) return true;
      return String(p.name).toLowerCase().includes(q) || String(p.sku || "").toLowerCase().includes(q) || String(p.serial_no || "").toLowerCase().includes(q);
    });
  }, [products, search, item.products]);

  const save = async () => {
    if (!selectedProductId) return;
    setSaving(true);
    setError(null);
    try {
      await api.put(`/new-joiner-requests/${item.request_id}/items/${item.id}/inventory`, {
        products: [{ product_id: Number(selectedProductId), qty: item.qty }],
      });
      setOpen(false);
      onSaved();
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to reserve inventory.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Button className="njr-select-btn" size="sm" variant={item.status === "FULFILLED" ? "secondary" : "primary"} icon={<FaBoxOpen />} onClick={() => setOpen(true)}>
        {item.status === "FULFILLED" ? "Change Product" : "Select Inventory"}
      </Button>
      <Modal show={open} onClose={() => setOpen(false)} title={`Select inventory for ${item.asset_type_name} x${item.qty}`}
        footer={(
          <>
            <Button variant="secondary" onClick={() => setOpen(false)}>Cancel</Button>
            <Button variant="primary" loading={saving} disabled={!selectedProductId} onClick={save}>Save</Button>
          </>
        )}
      >
        {error && <ErrorState message={error} />}
        <input
          className="ui-search-input"
          style={{ width: "100%", marginBottom: 10 }}
          placeholder="Search by product name, SKU or serial number..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        {loading ? <LoadingState /> : (
          <div style={{ maxHeight: 320, overflowY: "auto" }}>
            {filtered.length === 0 && <p className="text-muted">No available products found.</p>}
            {filtered.map((p) => (
              <label key={p.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 4px", borderBottom: "1px solid var(--border, #e7ded0)", cursor: "pointer" }}>
                <input type="radio" name={`product-${item.id}`} value={p.id} checked={Number(selectedProductId) === Number(p.id)} onChange={() => setSelectedProductId(p.id)} />
                <span style={{ flex: 1 }}>
                  <strong>{p.name}</strong>{" "}
                  <span className="text-muted">SKU: {p.sku}{p.serial_no ? ` · Serial: ${p.serial_no}` : ""}</span>
                </span>
                <span className="ui-badge ui-badge-blue">{p.available_qty} available</span>
              </label>
            ))}
          </div>
        )}
      </Modal>
    </>
  );
}

// Manual "Link Employee Account" picker (task spec §4): searches existing
// users by name/username/email and shows enough info (name, username,
// email, user id) that the technician can tell people with the same name
// apart, rather than ever auto-linking on name/username alone.
function LinkEmployeeAccountModal({ show, onClose, detail, onLinked }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!show) return;
    setQuery(detail?.employee_username || "");
    setSelectedUserId(null);
    setError(null);
  }, [show, detail]);

  useEffect(() => {
    if (!show) return;
    const q = query.trim();
    if (q.length < 2) {
      setResults([]);
      return;
    }
    setLoading(true);
    const handle = setTimeout(() => {
      api.get(`/new-joiner-requests/${detail.id}/employee-account-candidates`, { params: { q } })
        .then((res) => setResults(Array.isArray(res.data?.data) ? res.data.data : []))
        .catch(() => setResults([]))
        .finally(() => setLoading(false));
    }, 300);
    return () => clearTimeout(handle);
  }, [query, show, detail]);

  const confirm = async () => {
    if (!selectedUserId) return;
    setSaving(true);
    setError(null);
    try {
      await api.post(`/new-joiner-requests/${detail.id}/link-employee-account`, { user_id: selectedUserId });
      onLinked();
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to link employee account.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show={show} onClose={onClose} title="Link Employee Account"
      footer={(
        <>
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button variant="primary" loading={saving} disabled={!selectedUserId} onClick={confirm}>Link Selected Account</Button>
        </>
      )}
    >
      {error && <ErrorState message={error} />}
      <p className="text-muted" style={{ marginBottom: 10 }}>
        Search by name, username or email and select the correct account for <strong>{detail?.employee_name}</strong>.
      </p>
      <input
        className="ui-search-input"
        style={{ width: "100%", marginBottom: 10 }}
        placeholder="Search by name, username or email..."
        value={query}
        onChange={(e) => setQuery(e.target.value)}
      />
      {loading ? <LoadingState /> : (
        <div style={{ maxHeight: 320, overflowY: "auto" }}>
          {query.trim().length < 2 && <p className="text-muted">Type at least 2 characters to search.</p>}
          {query.trim().length >= 2 && results.length === 0 && <p className="text-muted">No matching users found.</p>}
          {results.map((u) => (
            <label key={u.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 4px", borderBottom: "1px solid var(--border, #e7ded0)", cursor: "pointer" }}>
              <input
                type="radio"
                name="employee-account-candidate"
                value={u.id}
                checked={Number(selectedUserId) === Number(u.id)}
                onChange={() => setSelectedUserId(u.id)}
              />
              <span style={{ flex: 1 }}>
                <strong>{u.name}</strong>
                <br />
                <span className="text-muted">
                  Username: {u.username || "-"} · Email: {u.email || "-"} · User ID: {u.id}
                </span>
              </span>
            </label>
          ))}
        </div>
      )}
    </Modal>
  );
}

function DeployModal({ show, onClose, detail, onDeployed }) {
  const [deploymentDate, setDeploymentDate] = useState(todayIsoDate());
  const [remarks, setRemarks] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const deploy = async () => {
    setSaving(true);
    setError(null);
    try {
      await api.post(`/new-joiner-requests/${detail.id}/deploy`, { deployment_date: deploymentDate, deployment_remarks: remarks });
      onDeployed();
    } catch (err) {
      setError(err?.response?.data?.message || err?.response?.data?.code || "Failed to deploy.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show={show} onClose={onClose} title="Deploy Assets"
      footer={(
        <>
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button variant="primary" loading={saving} onClick={deploy}>Confirm Deployment</Button>
        </>
      )}
    >
      {error && <ErrorState message={error} />}
      <p><strong>Employee:</strong> {detail.employee_name}</p>
      <p><strong>Joining Date:</strong> {formatDateOnly(detail.joining_date)}</p>
      <p><strong>Assets:</strong></p>
      <ul>
        {(detail.items || []).flatMap((i) => i.products || []).map((p) => (
          <li key={p.id}>{p.product_name} ({p.sku})</li>
        ))}
      </ul>
      <div style={{ marginBottom: 12 }}>
        <label style={{ display: "block", fontWeight: 600, fontSize: 13, marginBottom: 4 }}>Deployment Date *</label>
        <input type="date" style={{ width: "100%", padding: 8, border: "1px solid var(--border, #e7ded0)", borderRadius: 6 }} value={deploymentDate} max={todayIsoDate()} onChange={(e) => setDeploymentDate(e.target.value)} />
      </div>
      <div>
        <label style={{ display: "block", fontWeight: 600, fontSize: 13, marginBottom: 4 }}>Deployment Remarks</label>
        <textarea style={{ width: "100%", padding: 8, border: "1px solid var(--border, #e7ded0)", borderRadius: 6 }} rows={3} value={remarks} onChange={(e) => setRemarks(e.target.value)} />
      </div>
    </Modal>
  );
}

export default function NewJoinerRequestDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [actionBusy, setActionBusy] = useState(false);
  const [showDeploy, setShowDeploy] = useState(false);
  const [showLinkAccount, setShowLinkAccount] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/new-joiner-requests/${id}`)
      .then((res) => { setDetail(res.data?.data || null); setError(null); })
      .catch((err) => setError(err?.response?.data?.message || "Failed to load request."))
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const runAction = async (fn) => {
    setActionBusy(true);
    setActionError(null);
    try {
      await fn();
      load();
    } catch (err) {
      setActionError(err?.response?.data?.message || "Action failed.");
    } finally {
      setActionBusy(false);
    }
  };

  if (loading) return <LoadingState />;
  if (error || !detail) return <ErrorState message={error || "Request not found."} />;

  const allFulfilled = (detail.items || []).every((i) => i.status === "FULFILLED");
  const employeeAccountMissing = !detail.employee_user_id && !detail.linked_employee_username;

  const showAccountAlert = employeeAccountMissing && detail.status !== "DEPLOYED" && detail.status !== "CANCELLED";
  const itemsEmpty = (detail.items || []).length === 0;
  const assetColumns = [
    { key: "asset_type", label: "Asset Type" },
    { key: "qty", label: "Qty", align: "center" },
    { key: "requirement", label: "Requirement" },
    { key: "selected_product", label: "Selected Product" },
    { key: "action", label: "Action", align: "center" },
  ];

  return (
    <div className="njr-detail-page">
      <Button className="njr-back-btn" variant="secondary" size="sm" icon={<FaArrowLeft />} onClick={() => navigate(-1)}>Back</Button>

      <Card
        className="njr-header-card"
        flush
        title={(
          <span className="njr-header-title">
            <strong>{detail.request_number}</strong>
            <small>{detail.employee_name}</small>
          </span>
        )}
        actions={<span className={`ui-badge ui-badge-${STATUS_TONE[detail.status] || "gray"}`}>{STATUS_LABEL[detail.status] || detail.status}</span>}
      />

      <Card className="njr-main-card">
        {actionError && <ErrorState message={actionError} />}

        <div className="njr-section njr-info-grid">
          <InfoCard title="Employee Information">
            <InfoRow label="Name" value={detail.employee_name} />
            <InfoRow label="Username" value={detail.employee_username} />
            <InfoRow label="Employee Code" value={detail.employee_code || "-"} />
            <InfoRow label="Official Email" value={detail.official_email || "-"} />
            <InfoRow label="Designation" value={detail.designation} />
            <InfoRow label="Company" value={detail.company_name} />
            <InfoRow label="Department" value={detail.department_name || "-"} />
            <InfoRow label="Location" value={detail.location || "-"} />
          </InfoCard>
          <InfoCard title="Joining Information">
            <InfoRow label="Joining Date" value={formatDateOnly(detail.joining_date)} />
            <InfoRow label="Submitted" value={formatDateTime(detail.submitted_at)} />
            {detail.deployment_date && <InfoRow label="Deployed" value={formatDateOnly(detail.deployment_date)} />}
            {detail.remarks && <InfoRow label="Remarks" value={detail.remarks} />}
          </InfoCard>
          <InfoCard title="Manager / HR">
            <InfoRow label="Reporting Manager" value={detail.manager_name || "-"} />
            <InfoRow label="Manager Email" value={detail.manager_email || "-"} />
            <InfoRow label="Requester" value={detail.requester_name || "-"} />
            <InfoRow label="Requester Email" value={detail.requester_email || "-"} />
          </InfoCard>
        </div>

        {showAccountAlert && (
          <div className="njr-section njr-alert-card">
            <span className="njr-alert-icon"><FaExclamationTriangle aria-hidden="true" /></span>
            <p className="njr-alert-message">Employee user account has not been created yet. Assets can remain Ready, but final system allocation requires the employee account.</p>
            <Button icon={<FaLink />} onClick={() => setShowLinkAccount(true)}>
              Link Employee Account
            </Button>
          </div>
        )}

        <div className="njr-section">
          <h4 className="njr-section-title">Requested Assets</h4>
          <DataTable columns={assetColumns} loading={false} empty={itemsEmpty} emptyMessage="No assets requested.">
            {(detail.items || []).map((item) => (
              <tr key={item.id}>
                <td>{item.asset_type_name}</td>
                <td className="ui-td-center">{item.qty}</td>
                <td>{item.requirement_note || "-"}</td>
                <td>{(item.products || []).map((p) => `${p.product_name} (${p.sku})`).join(", ") || "-"}</td>
                <td className="ui-td-center">
                  {["ASSIGNED", "IN_PREPARATION"].includes(detail.status) && (
                    <ItemInventoryPicker item={{ ...item, request_id: detail.id }} unitId={detail.unit_id} onSaved={load} />
                  )}
                </td>
              </tr>
            ))}
          </DataTable>
        </div>

        <div className="njr-section">
          <h4 className="njr-section-title">Timeline</h4>
          {(detail.history || []).length === 0 ? (
            <p className="njr-empty-timeline">No activity recorded yet.</p>
          ) : (
            <div className="njr-timeline">
              {(detail.history || []).map((h) => (
                <div className={`njr-timeline-item${timelineTone(h.event) ? ` njr-timeline-item--${timelineTone(h.event)}` : ""}`} key={h.id}>
                  <span className="njr-timeline-dot" aria-hidden="true" />
                  <div className="njr-timeline-head">
                    <span className="njr-timeline-event">{h.event}</span>
                    <span className="njr-timeline-time">{formatDateTime(h.changed_at)}</span>
                  </div>
                  <p className="njr-timeline-desc">
                    {h.changed_by_name ? `By ${h.changed_by_name}` : h.actor_label ? `(${h.actor_label})` : ""}
                    {h.remarks ? `${h.changed_by_name || h.actor_label ? " — " : ""}${h.remarks}` : ""}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      </Card>

      <div className="njr-actions-bar">
        {["SUBMITTED", "ASSIGNED"].includes(detail.status) && (
          <Button variant="primary" loading={actionBusy} onClick={() => runAction(() => api.post(`/new-joiner-requests/${id}/start-preparation`))}>
            Start Preparation
          </Button>
        )}
        {detail.status === "IN_PREPARATION" && (
          <Button variant="primary" disabled={!allFulfilled} loading={actionBusy} onClick={() => runAction(() => api.post(`/new-joiner-requests/${id}/ready`))}>
            Mark Ready
          </Button>
        )}
        {detail.status === "READY" && (
          <Button variant="primary" icon={<FaCheckCircle />} onClick={() => setShowDeploy(true)}>
            Deploy Assets
          </Button>
        )}
        {!["DEPLOYED", "CANCELLED"].includes(detail.status) && (
          <Button variant="secondary" loading={actionBusy} onClick={() => {
            // Pre-filled with the SAME calendar day shown above -- never a
            // sliced UTC timestamp (that was the previous day, and
            // accepting the prompt saved it).
            const newDate = window.prompt("New joining date (YYYY-MM-DD):", toDateOnlyString(detail.joining_date) || "");
            if (!newDate) return;
            const reason = window.prompt("Reason for the change (optional):") || undefined;
            runAction(() => api.put(`/new-joiner-requests/${id}/joining-date`, { joining_date: newDate, reason }));
          }}>
            Change Joining Date
          </Button>
        )}
        {!["DEPLOYED", "CANCELLED"].includes(detail.status) && (
          <Button variant="danger" loading={actionBusy} onClick={() => {
            const reason = window.prompt("Reason for cancellation:");
            if (reason === null) return;
            runAction(() => api.post(`/new-joiner-requests/${id}/cancel`, { reason }));
          }}>
            Cancel Request
          </Button>
        )}
      </div>

      <DeployModal show={showDeploy} detail={detail} onClose={() => setShowDeploy(false)} onDeployed={() => { setShowDeploy(false); load(); }} />
      <LinkEmployeeAccountModal show={showLinkAccount} detail={detail} onClose={() => setShowLinkAccount(false)} onLinked={() => { setShowLinkAccount(false); load(); }} />
    </div>
  );
}
